// src/lib/recurringJobs.js
// Canonical recurring-job generation logic. Extracted from Jobs.js's
// handleRecurring() so the Schedule tab's Recurring Jobs list (next-due date,
// overdue flag, "Skip This Occurrence") uses the exact same cadence math
// instead of a second copy that could silently drift from it.

import { parseLocalDate, toDateStr } from './dates'

const FREQUENCY_DAYS = { weekly: 7, biweekly: 14 }

export function computeNextDate(baseDateStr, frequency) {
  if (!baseDateStr) return null
  const next = parseLocalDate(baseDateStr)
  if (FREQUENCY_DAYS[frequency]) next.setDate(next.getDate() + FREQUENCY_DAYS[frequency])
  else if (frequency === 'monthly') next.setMonth(next.getMonth() + 1)
  else if (frequency === 'quarterly') next.setMonth(next.getMonth() + 3)
  else return null
  return next
}

// Generates the next occurrence for a completed/skipped recurring job.
// Idempotent: if this occurrence has already spawned a successor (a job whose
// recurrence_parent_id points at it), it does nothing instead of creating a
// second one -- this is what makes it safe to call from multiple UI paths
// (status dropdown, quick-advance button, popup save, skip) without every
// caller having to separately guard against double-firing.
export async function generateNextOccurrence(supabase, job, { createdBy, parentId } = {}) {
  if (!job?.is_recurring || !job?.recurrence_frequency) return null
  const rootId = parentId ?? job.id
  const { data: existing, error: existingErr } = await supabase
    .from('jobs').select('id').eq('recurrence_parent_id', rootId).limit(1).maybeSingle()
  if (existingErr) throw existingErr
  if (existing) return null
  // No scheduled_date (job was never dated) still needs to keep recurring --
  // anchor on today rather than silently refusing to generate a successor.
  const baseDate = job.scheduled_date || toDateStr(new Date())
  const next = computeNextDate(baseDate, job.recurrence_frequency)
  if (!next) return null
  const { data, error } = await supabase.from('jobs').insert([{
    title: job.title, client_id: job.client_id, job_type: job.job_type,
    status: 'pending', priority: job.priority,
    scheduled_date: toDateStr(next),
    address: job.address, description: job.description,
    contract_amount: job.contract_amount, crew_size: job.crew_size,
    estimated_hours: job.estimated_hours, is_recurring: true,
    recurrence_frequency: job.recurrence_frequency,
    recurrence_parent_id: rootId, notes: job.notes, created_by: createdBy
  }]).select().single()
  if (error) throw error
  return data
}

// The single enforcement point for "completing a recurring job generates its
// successor." Every UI path that can mark a job completed (Jobs.js status
// dropdown, Jobs.js edit-modal save, Schedule.js quick-advance, Schedule.js
// popup save) should call this instead of deciding on its own whether to
// generate -- previously each path re-implemented (or forgot) the
// wasCompleted guard, which is exactly how the edit-modal path ended up never
// generating a successor at all. Never throws -- a failed follow-on
// generation shouldn't undo a completion the user already made; the caller
// gets `recurrenceError` back to show a non-fatal warning.
export async function maybeGenerateOnCompletion(supabase, job, newStatus, wasCompleted, { createdBy } = {}) {
  if (newStatus !== 'completed' || wasCompleted || !job.is_recurring) return { recurrenceError: null }
  try {
    await generateNextOccurrence(supabase, { ...job, status: newStatus }, { createdBy })
    return { recurrenceError: null }
  } catch (e) {
    return { recurrenceError: e }
  }
}

// The next due visit for the Recurring Jobs list. A not-yet-completed occurrence's
// own scheduled_date IS the next due visit; a completed one projects one cadence
// forward from it.
export function nextDueDate(job) {
  if (!job.scheduled_date) return null
  if (job.status !== 'completed') return parseLocalDate(job.scheduled_date)
  return computeNextDate(job.scheduled_date, job.recurrence_frequency)
}

export function daysOverdue(job) {
  const due = nextDueDate(job)
  if (!due) return 0
  const today = new Date(); today.setHours(0, 0, 0, 0)
  due.setHours(0, 0, 0, 0)
  const diff = Math.round((today - due) / 86400000)
  return diff > 0 ? diff : 0
}

// Walks recurrence_parent_id links back to the root of a recurring series, so
// the Recurring Jobs list can group by "the same series" exactly instead of
// the client+job_type heuristic, which still collapses two distinct series of
// the same type for one client (e.g. front yard weekly + back yard weekly).
export function seriesRootId(job, jobsById) {
  const visited = new Set()
  let current = job
  while (current?.recurrence_parent_id && !visited.has(current.id)) {
    visited.add(current.id)
    const parent = jobsById.get(current.recurrence_parent_id)
    if (!parent) break
    current = parent
  }
  return current.id
}
